import { useState } from 'react';
import moment from 'moment';
import { Paper, Stack } from '@mui/material';
import { useForm, useWatch } from 'react-hook-form';
import { useQuery } from '@apollo/client';
import { ModalsKeys } from 'global';
import { PageContainer } from 'components/shared/Page/PageContainer';
import { ModalsContainer } from 'components/shared/Modals';
import { useModal } from 'hooks/useModal';
import { AgentActivityLogOutcome, AgentActivityLogProcessType } from 'types/generated';
import { AssistantLogFilters as FiltersForm, AssistantLogRow, QuickDatePreset } from './types';
import { AssistantLogFilters } from './AssistantLogFilters';
import { AssistantLogTable } from './AssistantLogTable';
import { GET_AGENT_ACTIVITY, GetAgentActivityResponse } from './query';
import { mapAgentActivityToRow } from './mapper';
import { ActivityDetailsDialog } from './ActivityDetailsDialog';

const modalsWithKeys = {
  [ModalsKeys.AssistantLogActivityDetailsDialog]: <ActivityDetailsDialog />,
};

export const AssistantLogPage = () => {
  const [activePreset, setActivePreset] = useState<QuickDatePreset | null>('lastWeek');

  const { handleOpen: openActivityDetails } = useModal(ModalsKeys.AssistantLogActivityDetailsDialog);

  const { control, setValue } = useForm<FiltersForm>({
    defaultValues: {
      from: moment().subtract(7, 'days').startOf('day'),
      to: moment().endOf('day'),
      outcome: '',
      processType: '',
    },
  });

  const [from, to, outcome, processType] = useWatch({
    control,
    name: ['from', 'to', 'outcome', 'processType'],
  });

  const { data, loading } = useQuery<GetAgentActivityResponse>(GET_AGENT_ACTIVITY, {
    skip: !from || !to,
    variables: {
      input: {
        from: from?.toISOString(),
        to: to?.toISOString(),
        outcome: (outcome || null) as AgentActivityLogOutcome | null,
        processType: (processType || null) as AgentActivityLogProcessType | null,
      },
    },
  });

  const activities = data?.getAgentActivity ?? [];
  const rows = activities.map(mapAgentActivityToRow);

  const handleViewDetails = (row: AssistantLogRow) => {
    const activity = activities.find(a => a.agentActivityLogId === row.id);
    openActivityDetails({
      agentActivityLogId: row.id,
      processType: row.processType,
      documentType: row.documentType,
      outcome: row.outcome,
      trigger: (activity?.trigger as Record<string, unknown>) ?? null,
      payload: (activity?.payload as Record<string, unknown>) ?? null,
      error: (activity?.error as Record<string, unknown>) ?? null,
      associations: (activity?.associations as Record<string, unknown>) ?? null,
      reviewReasons: (activity?.reviewReasons as string[] | undefined) ?? [],
    });
  };

  return (
    <PageContainer>
      <Stack spacing={2} height="100%">
        <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1, gap: 2, overflow: 'hidden' }}>
          <AssistantLogFilters
            control={control}
            setValue={setValue}
            activePreset={activePreset}
            onPresetChange={setActivePreset}
          />

          <AssistantLogTable rows={rows} isLoading={loading} onViewDetails={handleViewDetails} />
        </Paper>
      </Stack>

      <ModalsContainer modalsWithKeys={modalsWithKeys} />
    </PageContainer>
  );
};
